import { BorderBox, GradientLine } from "./";
import { useMemo } from "react";
import { TrashIcon } from "../assets/icons/index.js";

export default function Notes({
  onDeleteNote,
  onHighlightClick,
  loading,
  annotations = {},
  archive = [],
  onDeleteArchivedAnnotation,
}) {
  // Derive the list straight from the annotation data (each annotation carries
  // its own persisted `text`). No DOM scraping and no remount on every change —
  // that re-scan + full remount was a major contributor to the highlight freeze.
  const highlights = useMemo(() => {
    if (loading) return [];
    return Object.entries(annotations)
      .map(([key, value]) => ({
        id: key,
        ...value,
        text: value.text || "",
      }))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [annotations, loading]);

  const getHighlightColor = (color) => {
    switch (color) {
      case "yellow":
        return "bg-lz-yellow";
      case "green":
        return "bg-lz-light-green";
      case "pink":
        return "bg-lz-light-pink";
      default:
        return "bg-gray-200";
    }
  };

  return (
    <BorderBox overflow>
      <h2 className="text-lz-ink-mid dark:text-white text-2xl mb-4">
        Your Highlights
      </h2>
      <div className="space-y-4">
        {highlights.map((highlight) => (
          <div key={highlight.id} className="text-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-gray-400">
                {new Date(highlight.timestamp).toLocaleString()}
              </span>
            </div>
            <div
              onClick={() => onHighlightClick(highlight.id)}
              className={`text-black ${getHighlightColor(
                highlight.color,
              )} p-2 rounded mb-2 text-sm cursor-pointer transition-shadow duration-200 hover:shadow-[0_0_8px_2px_rgba(255,255,255,0.5)]`}
            >
              {highlight.text}
            </div>
            {highlight.note && (
              <div className="flex items-center gap-2 pl-6">
                <p className="text-sm text-lz-ink dark:text-lz-concrete flex-grow break-words max-w-[calc(100%-2rem)]">
                  {highlight.note}
                </p>
                <button
                  onClick={() => onDeleteNote(highlight.id)}
                  title="Delete note"
                >
                  <TrashIcon className="m-auto h-4 w-4 fill-lz-ink-mid dark:fill-lz-concrete hover:fill-red-400" />
                </button>
              </div>
            )}
          </div>
        ))}
        {highlights.length === 0 && (
          <p className="text-gray-400 text-sm">No highlights on this page</p>
        )}
      </div>
      {archive.length > 0 && (
        <>
          <GradientLine className="my-6" />
          <h3 className="text-lz-ink-mid dark:text-white text-xl mb-2">
            Archive
          </h3>
          <p className="text-base text-gray-500 dark:text-gray-200 mb-4">
            These notes have been unlinked from the associated lesson because
            changes have been made to the lesson.
          </p>
          <div className="space-y-4">
            {archive
              .flatMap((blob, blobIndex) =>
                Object.entries(blob).map(([key, value]) => ({
                  id: key,
                  blobIndex,
                  ...value,
                })),
              )
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((entry) => (
                <div key={entry.id} className="text-white mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-400">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 p-2 rounded text-sm flex-grow">
                      {entry.text}
                    </div>
                    <button
                      onClick={() =>
                        onDeleteArchivedAnnotation(entry.blobIndex, entry.id)
                      }
                      title="Delete archived entry"
                      className="shrink-0"
                    >
                      <TrashIcon className="h-4 w-4 fill-lz-ink-mid dark:fill-lz-concrete hover:fill-red-400" />
                    </button>
                  </div>
                  {entry.note && (
                    <div className="pl-6 mb-2">
                      <p className="text-sm text-lz-ink dark:text-lz-concrete break-words">
                        {entry.note}
                      </p>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}
    </BorderBox>
  );
}
